import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  Button,
  Chip,
  Skeleton,
  Divider,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  Memory as MemoryIcon,
  Public as PublicIcon,
} from "@mui/icons-material";
import { fetchUsage, fetchUsageBreakdown } from "../redux/slices/usageSlice";
import ResourceGauge from "../components/usage/ResourceGauge";
import { SPACING } from "../theme/constants";

export default function Usage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { usage, breakdown, loading, breakdownLoading } = useSelector(
    (state) => state.usage,
  );

  useEffect(() => {
    dispatch(fetchUsage());
    dispatch(fetchUsageBreakdown());
  }, [dispatch]);

  if (loading && !usage) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <Skeleton variant="rectangular" height={120} borderRadius={2} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: SPACING.xl,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Resource Usage
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {usage?.plan
              ? `${usage.plan.name} Plan — $${usage.plan.priceMonthly}/mo`
              : "View your organization's resource consumption"}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate("/dashboard/billing")}
        >
          Manage Plan
        </Button>
      </Box>

      {/* Resource Gauges */}
      {usage && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: { xs: 3, md: 4 },
            p: { xs: 2.5, md: 3 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "background.paper",
            mb: SPACING.xxl,
          }}
        >
          <Box>
            <ResourceGauge
              label="Sites"
              used={usage.sites.used}
              limit={usage.sites.limit}
              pct={usage.sites.pct}
            />
          </Box>
          <Box>
            <ResourceGauge
              label="Docker Apps"
              used={usage.apps.used}
              limit={usage.apps.limit}
              pct={usage.apps.pct}
            />
          </Box>
          <Box>
            <ResourceGauge
              label="Storage"
              used={usage.storage.usedGB}
              limit={usage.storage.limitGB}
              unit="GB"
              pct={usage.storage.pct}
            />
          </Box>
          <Box>
            <ResourceGauge
              label="Bandwidth"
              used={usage.bandwidth.usedGB}
              limit={usage.bandwidth.limitGB}
              unit="GB/mo"
              pct={usage.bandwidth.pct}
            />
          </Box>
        </Box>
      )}

      {/* Compute Allocation + Other Limits */}
      {usage && (
        <Grid container spacing={2} sx={{ mb: SPACING.xxl }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                p: { xs: 2, md: 3 },
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "background.paper",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MemoryIcon fontSize="small" sx={{ color: "white" }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Compute Allocation <Typography variant="caption" color="text.secondary">(per site/service)</Typography>
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "action.hover",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1, fontWeight: 600, textTransform: "uppercase" }}
                    >
                      WordPress Site
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <Box component="span" fontWeight={500}>CPU:</Box> {usage.compute.wpCpuPerSite} cores
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <Box component="span" fontWeight={500}>Memory:</Box>{" "}
                      {usage.compute.wpMemPerSite >= 1024
                        ? `${usage.compute.wpMemPerSite / 1024} GB`
                        : `${usage.compute.wpMemPerSite} MB`}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <Box component="span" fontWeight={500}>DB CPU:</Box> {usage.compute.wpDbCpuPerSite} cores
                    </Typography>
                    <Typography variant="body2">
                      <Box component="span" fontWeight={500}>DB Memory:</Box>{" "}
                      {usage.compute.wpDbMemPerSite >= 1024
                        ? `${usage.compute.wpDbMemPerSite / 1024} GB`
                        : `${usage.compute.wpDbMemPerSite} MB`}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "action.hover",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1, fontWeight: 600, textTransform: "uppercase" }}
                    >
                      App Service
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <Box component="span" fontWeight={500}>CPU:</Box> {usage.compute.appCpuPerSvc} cores
                    </Typography>
                    <Typography variant="body2">
                      <Box component="span" fontWeight={500}>Memory:</Box>{" "}
                      {usage.compute.appMemPerSvc >= 1024
                        ? `${usage.compute.appMemPerSvc / 1024} GB`
                        : `${usage.compute.appMemPerSvc} MB`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                p: { xs: 2, md: 3 },
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "background.paper",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    bgcolor: "secondary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TrendingUpIcon fontSize="small" sx={{ color: "white" }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Plan Limits
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <LimitRow
                    label="Services/App"
                    value={usage.limits.maxServicesPerApp}
                  />
                  <LimitRow
                    label="Volumes/Service"
                    value={usage.limits.maxVolumesPerSvc}
                  />
                  <LimitRow
                    label="Backup Retention"
                    value={`${usage.limits.maxBackupRetention} days`}
                  />
                </Grid>
                <Grid size={6}>
                  <LimitRow
                    label="SFTP Users/Site"
                    value={usage.limits.maxSftpUsersPerSite}
                  />
                  <LimitRow
                    label="SSH Keys"
                    value={usage.limits.maxSshKeys}
                  />
                  <LimitRow
                    label="API Tokens"
                    value={usage.limits.maxApiTokens}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      )}

      <Divider sx={{ mb: SPACING.xl }} />

      {/* Per-Site Breakdown */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        Per-Site Breakdown
      </Typography>

      {breakdownLoading ? (
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
      ) : breakdown && breakdown.length > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {breakdown.map((site) => {
            const isOnline = site.status === "ACTIVE";
            return (
              <Box
                key={site.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      bgcolor: isOnline ? "success.light" : "action.selected",
                      color: isOnline ? "success.dark" : "text.secondary",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <PublicIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={600} noWrap>
                        {site.name}
                      </Typography>
                      <Chip
                        label={site.status}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          bgcolor: isOnline ? "success.main" : "grey.500",
                          color: "white",
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                      {site.domain || "—"}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: "flex", gap: 4, textAlign: "right" }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Storage
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {site.storageUsedMB >= 1024
                        ? `${(site.storageUsedMB / 1024).toFixed(1)} GB`
                        : `${site.storageUsedMB} MB`}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Bandwidth
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {site.bandwidthUsedGB > 0
                        ? `${site.bandwidthUsedGB.toFixed(1)} GB`
                        : "—"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box
          sx={{
            p: 4,
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 2,
            textAlign: "center",
            bgcolor: "background.default",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No sites found or no usage data available yet.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function LimitRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-child": {
          borderBottom: "none",
        },
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} fontFamily="monospace">
        {value}
      </Typography>
    </Box>
  );
}
